/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject, ComponentProps } from 'preact';
import { JSXInternal } from 'preact/src/jsx';
import { EnvironmentProvider } from '../../UNSAFE_Environment';
import { Coords, Floating, Placement } from '../../UNSAFE_Floating';
import { Layer } from '../../UNSAFE_Layer';
import { ColorScheme } from '../../UNSAFE_Theme';
import { TooltipManager } from '../../hooks/UNSAFE_useTooltip/TooltipManager';
import { UnsafeDomHandle, UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';

type FloatingProps = ComponentProps<typeof Floating>;

// Global TooltipManager singleton instance
const globalTooltipManager = new TooltipManager();

/**
 * The delays (in ms) for Redwood tooltips.
 */
export const SHOW_TOOLTIP_DELAY = 1000;
export const HIDE_TOOLTIP_DELAY = 150;

/**
 * Returns a global TooltipManager singleton instance
 */
export const getGlobalTooltipManager = (): TooltipManager => {
  return globalTooltipManager;
};

/**
 * Wraps the tooltip content provided in the appropriate layering and returns the result for display.
 */
export const getWrappedTooltipContent = (
  tooltipContent: JSXInternal.Element,
  variant: 'tooltip' | 'datatip',
  colorScheme: ColorScheme,
  anchorRef: RefObject<Element | Coords>,
  placement: Placement,
  offset?: { mainAxis?: number; crossAxis?: number }
): JSXInternal.Element => {
  const defaultOffset = 8; // Redwood tooltip has a default offset of 8 pixels
  const newOffset = offset ? offset : { mainAxis: defaultOffset, crossAxis: 0 };

  const newColorScheme = !colorScheme || colorScheme === 'light' ? 'dark' : 'light';

  const renderTooltipOrDatatip = () => {
    if (variant === 'tooltip') {
      return (
        <EnvironmentProvider environment={{ colorScheme: newColorScheme }}>
          {tooltipContent}
        </EnvironmentProvider>
      );
    } else {
      return <>{tooltipContent}</>;
    }
  };

  return (
    <Layer logicalParentRef={toLogicalParentRef(anchorRef)} priority="tooltip">
      <Floating anchorRef={anchorRef} placement={placement} offsetValue={newOffset}>
        {renderTooltipOrDatatip()}
      </Floating>
    </Layer>
  );
};

function toLogicalParentRef(anchorRef: FloatingProps['anchorRef']) {
  const current = anchorRef.current;

  if (current) {
    if (current instanceof Element) {
      return anchorRef as RefObject<Element>;
    } else if ((current as UnsafeDomHandle<Element>)[UNSAFE_DOM_ACCESS]) {
      return anchorRef as RefObject<UnsafeDomHandle<Element>>;
    }
  }

  return null;
}
